import type {
  WebSocketFactory,
  WebSocketLike,
  WpsCloseEvent,
  WpsMessageEvent,
} from '@github-ui/ahp-relay/webpubsub/wps-transport'

export interface AhpDebugEvent {
  id: string
  timestamp: string
  source: 'runtime' | 'mission-control' | 'relay' | 'websocket'
  direction: 'inbound' | 'outbound' | 'status'
  message: string
}

export type AhpDebugEventInput = Omit<AhpDebugEvent, 'id' | 'timestamp'>

let nextDebugEventId = 1

export function createDebugEvent(event: AhpDebugEventInput): AhpDebugEvent {
  const id = `debug-${nextDebugEventId}`
  nextDebugEventId += 1
  return {
    id,
    timestamp: new Date().toISOString(),
    ...event,
  }
}

export function formatDebugEvent(event: AhpDebugEvent): string {
  return `[${event.timestamp}] ${event.source}/${event.direction} ${event.message}`
}

export function formatDebugLog(events: readonly AhpDebugEvent[]): string {
  return events.map(formatDebugEvent).join('\n')
}

export function downloadDebugLog(debugLog: string, fileName = 'copilot-debug.log'): void {
  const blob = new Blob([debugLog], {type: 'text/plain;charset=utf-8'})
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')

  try {
    anchor.href = url
    anchor.download = fileName
    anchor.style.display = 'none'
    document.body.appendChild(anchor)
    anchor.click()
  } finally {
    anchor.remove()
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }
}

function debugFrameToString(data: unknown): string {
  if (typeof data === 'string') return data
  if (data instanceof ArrayBuffer) return new TextDecoder('utf-8').decode(data)
  if (ArrayBuffer.isView(data)) return new TextDecoder('utf-8').decode(data)
  return String(data)
}

function debugUrlToString(url: string): string {
  try {
    const baseUrl = typeof window === 'undefined' ? 'http://localhost' : window.location.origin
    const parsedUrl = new URL(url, baseUrl)
    parsedUrl.search = ''
    return parsedUrl.toString()
  } catch {
    return url.replace(/([?&]access_token=)[^&#]*/gi, '$1[REDACTED]')
  }
}

export function createDebugWebSocketFactory(onDebugEvent: (event: AhpDebugEventInput) => void): WebSocketFactory {
  return (url, subprotocol) => {
    onDebugEvent({source: 'websocket', direction: 'status', message: `connecting ${debugUrlToString(url)}`})
    const socket = new WebSocket(url, subprotocol) as unknown as WebSocketLike
    let onopen: (() => void) | null = null
    let onmessage: ((event: WpsMessageEvent) => void) | null = null
    let onclose: ((event: WpsCloseEvent) => void) | null = null
    let onerror: ((event: unknown) => void) | null = null

    socket.onopen = () => {
      onDebugEvent({source: 'websocket', direction: 'status', message: 'open'})
      onopen?.()
    }
    socket.onmessage = event => {
      onDebugEvent({
        source: 'websocket',
        direction: 'inbound',
        message: debugFrameToString(event.data),
      })
      onmessage?.(event)
    }
    socket.onclose = event => {
      onDebugEvent({
        source: 'websocket',
        direction: 'status',
        message: `close code=${event.code} reason=${event.reason}`,
      })
      onclose?.(event)
    }
    socket.onerror = event => {
      onDebugEvent({
        source: 'websocket',
        direction: 'status',
        message: `error ${String(event)}`,
      })
      onerror?.(event)
    }

    return {
      send(data: string) {
        onDebugEvent({source: 'websocket', direction: 'outbound', message: data})
        socket.send(data)
      },
      close() {
        onDebugEvent({source: 'websocket', direction: 'status', message: 'close requested'})
        socket.close()
      },
      get onopen() {
        return onopen
      },
      set onopen(value) {
        onopen = value
      },
      get onmessage() {
        return onmessage
      },
      set onmessage(value) {
        onmessage = value
      },
      get onclose() {
        return onclose
      },
      set onclose(value) {
        onclose = value
      },
      get onerror() {
        return onerror
      },
      set onerror(value) {
        onerror = value
      },
    }
  }
}
